// Family-clusters radial layout. Port of /tmp/clusters_reference.py (v6).
// Stage banners below correspond to the reference; algorithm is identical.

import { type DataLoader } from "../data/dataloader"
import { type Node } from "../node"
import { type Point, getPointOnCircle } from "../../utils"

// === Constants ===

export const RING_RADII = [80, 220, 360, 500, 640, 780]
const MIN_ARC_PX = 28
const RING_MIN_SLOT = RING_RADII.map(r => MIN_ARC_PX / r)
const SIB_PAD = (0.5 * Math.PI) / 180
const GROUP_PAD = (1.0 * Math.PI) / 180
const TOP_GAP = (3 * Math.PI) / 180

const GEN0_COUPLE_NAMES: [string, string][] = [
  ["Esperanza Maria Gomes Gomeze", "Quirino Meguel Gomeze"],
  ["Francisca Morales", "Fraucisco Morales"],
]
const MORALES_FOUNDER_NAMES = new Set(["Francisca Morales", "Fraucisco Morales"])
const MORALES_ROTATION = Math.PI / 2

type Placement = { angle: number, ring: number }

export function computeClusterPositions(dataLoader: DataLoader): Map<Node, Point> {
  const out = new Map<Node, Point>()
  const allNodes = dataLoader.nodes
  if (allNodes.length === 0) return out

  const byName = new Map<string, Node>()
  for (const n of allNodes) byName.set(n.person.name, n)

  const gen0Couples: [Node, Node][] = []
  for (const [a, b] of GEN0_COUPLE_NAMES) {
    const na = byName.get(a), nb = byName.get(b)
    if (na && nb) gen0Couples.push([na, nb])
  }
  if (gen0Couples.length === 0) return out

  // Main's generation calc seeds the oldest person at gen 1, so the founders
  // can be at gen 1 or higher. Normalise so the smallest gen maps to ring 0.
  const minGen = Math.min(...allNodes.map(n => n.generation))
  const ringOf = (n: Node) => n.generation - minGen
  const radiusOf = (n: Node) => RING_RADII[Math.min(ringOf(n), RING_RADII.length - 1)]
  const minSlotOf = (n: Node) => RING_MIN_SLOT[Math.min(ringOf(n), RING_MIN_SLOT.length - 1)]

  // === Stage 2: bloodline membership ===
  const descendantsOf = (founders: Node[]): Set<Node> => {
    const seen = new Set<Node>(founders)
    let frontier: Node[] = [...founders]
    while (frontier.length) {
      const next: Node[] = []
      for (const n of frontier) {
        for (const c of n.children) {
          if (!seen.has(c)) { seen.add(c); next.push(c) }
        }
      }
      frontier = next
    }
    return seen
  }

  const byCouple: Set<Node>[] = gen0Couples.map(c => descendantsOf(c))
  const allBlood = new Set<Node>()
  for (const s of byCouple) for (const n of s) allBlood.add(n)
  // Extend: a person whose parent is married to a bloodline person is bloodline too.
  let changed = true
  while (changed) {
    changed = false
    for (const n of allNodes) {
      if (allBlood.has(n)) continue
      for (const p of n.parents) {
        for (const sp of p.partners) {
          if (allBlood.has(sp)) { allBlood.add(n); changed = true; break }
        }
        if (allBlood.has(n)) break
      }
    }
  }

  // === Stage 2b: branch index per bloodline person ===
  const branchOf = new Map<Node, number>()
  for (const n of allBlood) {
    const inA = byCouple[0]?.has(n) ?? false
    const inB = byCouple[1]?.has(n) ?? false
    let idx = 0
    if (inA && !inB) idx = 0
    else if (inB && !inA) idx = 1
    else if (inA && inB) {
      idx = 0
      for (const par of n.parents) {
        if (byCouple[0]?.has(par)) { idx = 0; break }
        if (byCouple[1]?.has(par)) { idx = 1; break }
      }
    } else {
      // Bloodline via extension step — infer from parent or parent's spouse.
      outer: for (const par of n.parents) {
        if (byCouple[0]?.has(par)) { idx = 0; break }
        if (byCouple[1]?.has(par)) { idx = 1; break }
        for (const sp of par.partners) {
          if (byCouple[0]?.has(sp)) { idx = 0; break outer }
          if (byCouple[1]?.has(sp)) { idx = 1; break outer }
        }
      }
    }
    branchOf.set(n, idx)
  }

  // === Stage 3: primary parent + children map ===
  const primaryParent = new Map<Node, Node | null>()
  for (const n of allBlood) {
    const own = branchOf.get(n)
    let choice: Node | null = null
    // pass 1: same-branch bloodline parent
    for (const p of n.parents) {
      if (allBlood.has(p) && branchOf.get(p) === own) { choice = p; break }
    }
    // pass 2: any bloodline parent
    if (!choice) {
      for (const p of n.parents) {
        if (allBlood.has(p)) { choice = p; break }
      }
    }
    // pass 3: in-law parent's bloodline spouse
    if (!choice) {
      for (const p of n.parents) {
        for (const sp of p.partners) {
          if (allBlood.has(sp)) { choice = sp; break }
        }
        if (choice) break
      }
    }
    primaryParent.set(n, choice)
  }

  const childrenOf = new Map<Node, Node[]>()
  for (const [child, par] of primaryParent) {
    if (par) {
      const arr = childrenOf.get(par) ?? []
      arr.push(child)
      childrenOf.set(par, arr)
    }
  }

  // === Stage 4: demote cross-branch spouse ===
  // A bloodline person with no kids whose spouse is in a different founding
  // branch becomes an in-law of that spouse, so they sit beside their kids
  // instead of stranded in their original wedge. (Just Roque in our data.)
  const gen0NameSet = new Set<Node>(gen0Couples.flat())
  const demotedTo = new Map<Node, Node>()
  for (const n of allBlood) {
    if (gen0NameSet.has(n)) continue
    if (childrenOf.get(n)?.length) continue
    for (const sp of n.partners) {
      if (allBlood.has(sp)
          && !demotedTo.has(sp)
          && (childrenOf.get(sp)?.length ?? 0) > 0
          && branchOf.get(sp) !== branchOf.get(n)) {
        demotedTo.set(n, sp)
        break
      }
    }
  }
  for (const [par, kids] of childrenOf) {
    childrenOf.set(par, kids.filter(k => !demotedTo.has(k)))
  }

  // === Stage 5: attach in-laws to a bloodline anchor ===
  const inlawSpouse = new Map<Node, Node>()
  const needsAnchor: Node[] = []
  for (const n of allNodes) {
    if (!allBlood.has(n) || demotedTo.has(n)) needsAnchor.push(n)
  }
  // pass 1: direct bloodline spouse (or recorded demotion target)
  for (const n of needsAnchor) {
    const dem = demotedTo.get(n)
    if (dem) { inlawSpouse.set(n, dem); continue }
    for (const sp of n.partners) {
      if (allBlood.has(sp) && !demotedTo.has(sp)) { inlawSpouse.set(n, sp); break }
    }
  }
  // pass 2: partner-of-partner
  for (const n of needsAnchor) {
    if (inlawSpouse.has(n)) continue
    for (const sp of n.partners) {
      const a = inlawSpouse.get(sp)
      if (a) { inlawSpouse.set(n, a); break }
    }
  }
  // pass 3: via parents
  for (const n of needsAnchor) {
    if (inlawSpouse.has(n)) continue
    for (const par of n.parents) {
      if (allBlood.has(par) && !demotedTo.has(par)) { inlawSpouse.set(n, par); break }
      const a = inlawSpouse.get(par)
      if (a) { inlawSpouse.set(n, a); break }
    }
  }
  const inlawsFor = new Map<Node, Node[]>()
  for (const [inlaw, anchor] of inlawSpouse) {
    const arr = inlawsFor.get(anchor) ?? []
    arr.push(inlaw)
    inlawsFor.set(anchor, arr)
  }
  // Inject the gen-0 partner founder as an in-law of the primary founder.
  for (const [a, b] of gen0Couples) {
    const primary = a.children.length > 0 ? a : b
    const partner = primary === a ? b : a
    const arr = inlawsFor.get(primary) ?? []
    if (!arr.includes(partner)) arr.push(partner)
    inlawsFor.set(primary, arr)
  }

  // === Stage 6: child ordering + marriage grouping ===
  const dobKey = (n: Node | null | undefined): number => {
    const d = n?.person.birthDate
    if (!d) return 99_999_999
    return d.getFullYear() * 10_000 + (d.getMonth() + 1) * 100 + d.getDate()
  }
  for (const [par, kids] of childrenOf) {
    childrenOf.set(par, [...kids].sort((x, y) => dobKey(x) - dobKey(y)))
  }
  const groupChildrenBySpouse = (parent: Node, kids: Node[], inlaws: Node[]): {
    order: (Node | null)[], bySpouse: Map<Node | null, Node[]>
  } => {
    const inlawSet = new Set(inlaws)
    const bySpouse = new Map<Node | null, Node[]>()
    for (const kid of kids) {
      let other: Node | null = null
      for (const p of kid.parents) {
        if (p !== parent && inlawSet.has(p)) { other = p; break }
      }
      const arr = bySpouse.get(other) ?? []
      arr.push(kid)
      bySpouse.set(other, arr)
    }
    const order = [...bySpouse.keys()].sort((x, y) => dobKey(x) - dobKey(y))
    return { order, bySpouse }
  }

  // === Stages 7+8: subtree width bubbling ===
  const widthMemo = new Map<Node, number>()
  const subtreeWidth = (n: Node): number => {
    const cached = widthMemo.get(n)
    if (cached !== undefined) return cached
    const inlaws = inlawsFor.get(n) ?? []
    const ownMin = (1 + inlaws.length) * minSlotOf(n)
    const kids = childrenOf.get(n) ?? []
    if (kids.length === 0) { widthMemo.set(n, ownMin); return ownMin }
    const { order, bySpouse } = groupChildrenBySpouse(n, kids, inlaws)
    let kidsTotal = 0
    for (const sp of order) {
      const sks = bySpouse.get(sp)!
      kidsTotal += sks.reduce((a, k) => a + subtreeWidth(k), 0)
      kidsTotal += SIB_PAD * Math.max(0, sks.length - 1)
    }
    kidsTotal += GROUP_PAD * Math.max(0, order.length - 1)
    const w = Math.max(ownMin, kidsTotal)
    widthMemo.set(n, w)
    return w
  }

  // === Stage 9: recursive sub-wedge allocation + in-law placement ===
  const placement = new Map<Node, Placement>()
  const orderSelfBlock = (
    name: Node,
    inlaws: Node[],
    groupCenters: [Node | null, number][],
    inlawSet: Set<Node>,
    selfTarget: number,
    hasKids: boolean,
  ): Node[] => {
    if (!hasKids) {
      const h = Math.floor(inlaws.length / 2)
      return [...inlaws.slice(0, h), name, ...inlaws.slice(h)]
    }
    const withGroups = groupCenters.filter(([sp]) => sp !== null && inlawSet.has(sp))
    const withGroupSpouses = new Set(withGroups.map(([sp]) => sp!))
    const noGroups = inlaws.filter(il => !withGroupSpouses.has(il))
    if (withGroups.length === 0) {
      const h = Math.floor(inlaws.length / 2)
      return [...inlaws.slice(0, h), name, ...inlaws.slice(h)]
    }
    withGroups.sort((a, b) => a[1] - b[1])
    const left = withGroups.filter(([, gc]) => gc < selfTarget).map(([sp]) => sp!)
    const right = withGroups.filter(([, gc]) => gc >= selfTarget).map(([sp]) => sp!)
    const h = Math.floor(noGroups.length / 2)
    return [...noGroups.slice(0, h), ...left, name, ...right, ...noGroups.slice(h)]
  }

  const layoutSubtree = (name: Node, wStart: number, wEnd: number): number => {
    const kids = childrenOf.get(name) ?? []
    const inlaws = [...(inlawsFor.get(name) ?? [])]
    const inlawSet = new Set(inlaws)
    const wedgeSpan = wEnd - wStart
    const nBlock = 1 + inlaws.length
    const { order, bySpouse } = groupChildrenBySpouse(name, kids, inlaws)
    const groupCenters: [Node | null, number][] = []
    const childAngles: number[] = []
    let selfTarget: number
    if (kids.length > 0) {
      const perGroup = order.map(sp => {
        const sks = bySpouse.get(sp)!
        const ws = sks.map(k => subtreeWidth(k))
        return { sp, sks, ws }
      })
      let totalGroups = 0
      for (const g of perGroup) {
        totalGroups += g.ws.reduce((a, w) => a + w, 0) + SIB_PAD * Math.max(0, g.sks.length - 1)
      }
      totalGroups += GROUP_PAD * Math.max(0, order.length - 1)
      const scale = totalGroups > 0 ? Math.max(0.5, Math.min(1, wedgeSpan / totalGroups)) : 1
      let cursor = wStart
      for (const { sp, sks, ws } of perGroup) {
        const gStart = cursor
        let kc = gStart
        for (let i = 0; i < sks.length; i++) {
          const k = sks[i], kw = ws[i]
          const kwScaled = kw * scale
          const ca = layoutSubtree(k, kc, kc + kwScaled)
          childAngles.push(ca)
          kc += kwScaled + SIB_PAD
        }
        const gEnd = gStart + ws.reduce((a, w) => a + w * scale, 0) + SIB_PAD * Math.max(0, sks.length - 1)
        groupCenters.push([sp, (gStart + gEnd) / 2])
        cursor = gEnd + GROUP_PAD
      }
      selfTarget = childAngles.reduce((a, x) => a + x, 0) / childAngles.length
    } else {
      selfTarget = (wStart + wEnd) / 2
    }
    let slotW = minSlotOf(name)
    let blockW = slotW * nBlock
    if (blockW > wedgeSpan * 0.98) {
      blockW = wedgeSpan * 0.98
      slotW = blockW / nBlock
    }
    const orderedBlock = orderSelfBlock(name, inlaws, groupCenters, inlawSet, selfTarget, kids.length > 0)
    const halfBlock = blockW / 2
    const lo = wStart + halfBlock, hi = wEnd - halfBlock
    const blockCenter = lo > hi ? (wStart + wEnd) / 2 : Math.max(lo, Math.min(hi, selfTarget))
    const blockStart = blockCenter - halfBlock + slotW / 2
    for (let i = 0; i < orderedBlock.length; i++) {
      const person = orderedBlock[i]
      placement.set(person, { angle: blockStart + slotW * i, ring: ringOf(person) })
    }
    return placement.get(name)!.angle
  }

  // === Top-level wedge allocation ===
  const coupleRoots: { primary: Node, demand: number }[] = []
  for (const [a, b] of gen0Couples) {
    const primary = a.children.length > 0 ? a : b
    coupleRoots.push({ primary, demand: subtreeWidth(primary) })
  }
  const totalDemand = coupleRoots.reduce((a, c) => a + c.demand, 0)
  const available = 2 * Math.PI - TOP_GAP * coupleRoots.length
  const scale = totalDemand > 0 ? available / totalDemand : 1
  let cursor = -Math.PI / 2 - Math.PI / 3
  for (const { primary, demand } of coupleRoots) {
    const ws = demand * scale
    layoutSubtree(primary, cursor, cursor + ws)
    cursor += ws + TOP_GAP
  }

  // === Stage 10: Morales rotation ===
  const moralesIdx = gen0Couples.findIndex(([a, b]) =>
    MORALES_FOUNDER_NAMES.has(a.person.name) && MORALES_FOUNDER_NAMES.has(b.person.name))
  if (moralesIdx >= 0) {
    const reverseInlaw = new Map<Node, Node>()
    for (const [anchor, ils] of inlawsFor) for (const il of ils) reverseInlaw.set(il, anchor)
    const topBranchMemo = new Map<Node, number>()
    const topBranch = (n: Node): number => {
      const cached = topBranchMemo.get(n)
      if (cached !== undefined) return cached
      for (let i = 0; i < gen0Couples.length; i++) {
        if (gen0Couples[i].includes(n)) { topBranchMemo.set(n, i); return i }
      }
      const nxt = reverseInlaw.get(n) ?? primaryParent.get(n)
      const idx = nxt ? topBranch(nxt) : 0
      topBranchMemo.set(n, idx)
      return idx
    }
    for (const [n, p] of placement) {
      if (topBranch(n) === moralesIdx) p.angle += MORALES_ROTATION
    }
  }

  // === Cartesian conversion ===
  for (const [n, p] of placement) {
    out.set(n, getPointOnCircle(p.angle, RING_RADII[Math.min(p.ring, RING_RADII.length - 1)]))
  }
  return out
}
